import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LayoutContainer from 'components/Common/LayoutContainer'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import OfferCardCarouselSectionFilters from 'components/Common/OfferCardCarousel/OfferCardCarouselSectionFilters'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import Group from 'components/utils/Group'
import OfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChipLoadingBox from 'LuxKit/Chips/FilterChipLoadingBox'
import Heading from 'LuxKit/Typography/Heading'
import { SPEND_POINTS_CAROUSEL_RENDER_LIMIT } from './loyaltySpendPointsConstants'

const Section = styled(VerticalSpacer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

interface FilterOption {
  value: string
  label: string
}

interface Props {
  title: ReactNode
  viewAllTo: string
  filters?: App.OfferListFilters
  offerList?: App.OfferList
  filterOptions: Array<FilterOption>
  selectedFilter: string
  onFilterChange: (value: string) => void
  testId: string
  showFilters?: boolean
  filtersLoading?: boolean
  children?: ReactNode
}

function LoyaltySpendPointsCarouselSection(props: Props) {
  const {
    title,
    viewAllTo,
    filters,
    offerList,
    filterOptions,
    selectedFilter,
    onFilterChange,
    testId,
    showFilters = true,
    filtersLoading = false,
    children,
  } = props
  const intl = useIntl()

  return <Section gap={20} data-testid={testId}>
    <LayoutContainer>
      <VerticalSpacer gap={20}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
          <Heading variant="heading2">{title}</Heading>
          <TextButton kind="tertiary" size="medium" to={viewAllTo}>
            {intl.formatMessage({
              id: 'K_ZTryhU0UJ8rG88VDpX',
              defaultMessage: 'View all',
              description: 'Button label to view all items in a carousel section',
            })}
          </TextButton>
        </Group>
        {showFilters && (filtersLoading ? <HorizontalContainerWithFadedOverflow overflowGutter={16} gap={8}>
          {filterOptions.map(option => <FilterChipLoadingBox
            key={option.value}
            size="medium"
            width={Math.max(80, option.label.length * 8 + 24)}
          />)}
        </HorizontalContainerWithFadedOverflow> : <OfferCardCarouselSectionFilters
          options={filterOptions}
          value={selectedFilter}
          onChange={onFilterChange}
        />)}
      </VerticalSpacer>
    </LayoutContainer>
    {children ?? <OfferListCarousel
      tileStyle="card-default"
      tileSize="medium"
      filters={filters}
      overrideOfferList={offerList}
      limit={SPEND_POINTS_CAROUSEL_RENDER_LIMIT}
      tabletLimit={SPEND_POINTS_CAROUSEL_RENDER_LIMIT}
    >
      <OfferCarouselLookingForSomethingElse to={viewAllTo} />
    </OfferListCarousel>}
  </Section>
}

export default LoyaltySpendPointsCarouselSection
